import React, { useEffect } from "react";
import { Add, ExternalLink, PremiumFilled } from "@wix/wix-ui-icons-common/lazy";
import {
  Box,
  Image,
  Card,
  MarketingPageLayout,
  MarketingPageLayoutContent,
  Page,
  Loader,
  SkeletonGroup,
  SkeletonRectangle,
  ToggleSwitch,
  FormField,
  Text,
  Button,
  SectionHelper,
} from "@wix/design-system";
import { dashboard } from "@wix/dashboard";
import { createClient } from "@wix/sdk";
import { embeddedScripts, appInstances } from "@wix/app-management";
import MembersList from "./MembersList";
import { ReactTagManager } from "react-gtm-ts";
import { useTranslation } from "react-i18next";
import { initAnalytics, track } from "./analytics";

const tagManagerArgs = { code: "GTM-WZQPMC7" };

// Gravatar Toggle Component
// This component manages the state of the "Auto Populate Profile Images" toggle using KV storage
const GravatarToggle = () => {
  const { t } = useTranslation();
  const [isEnabled, setIsEnabled] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const API_BASE_URL = '/api';

  // Get the current toggle state from KV storage
  const getToggleState = async () => {
    try {
      const instance = new URLSearchParams(window.location.search).get('instance');
      const url = instance
        ? `${API_BASE_URL}/toggle-state?instance=${encodeURIComponent(instance)}`
        : `${API_BASE_URL}/toggle-state`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setIsEnabled(data.isEnabled);
      setIsLoading(false);
    } catch (err: any) {
      console.error('Error fetching toggle state:', err);
      setError(err.message || "Failed to get toggle state");
      setIsLoading(false);
    }
  };

  // Update toggle state in KV storage
  const updateToggleState = async (enabled: boolean) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const instance = new URLSearchParams(window.location.search).get('instance');
      const url = instance
        ? `${API_BASE_URL}/toggle-state?instance=${encodeURIComponent(instance)}`
        : `${API_BASE_URL}/toggle-state`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isEnabled: enabled }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setIsEnabled(data.isEnabled);
      setIsLoading(false);
    } catch (err: any) {
      console.error('Error updating toggle state:', err);
      setError(err.message || "Failed to update toggle state");
      setIsLoading(false);
    }
  };

  // Handle toggle change
  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateToggleState(event.target.checked);
  };

  // Load initial state
  React.useEffect(() => {
    getToggleState();
  }, []);

  if (isLoading) {
    return (
      <Box direction="vertical" gap="SP2">
        <Text size="small">{t("grav-loading-settings")}</Text>
        <Loader size="tiny" />
      </Box>
    );
  }

  return (
    <Box direction="vertical" gap="SP2">
      <FormField
        label={t("grav-toggle-label")}
        labelPlacement="left"
        stretchContent={false}
      >
        <ToggleSwitch
          checked={isEnabled}
          onChange={handleToggleChange}
          disabled={isLoading}
        />
      </FormField>
      {error && (
        <Text size="small" color="destructive">
          {error}
        </Text>
      )}
      <Text size="small" secondary>
        {isEnabled 
          ? t("grav-status-on")
          : t("grav-status-off")
        }
      </Text>
    </Box>
  );
};

// Information Component explaining how Gravatar works
const InfoSection = ({ siteUrl, siteId }: { siteUrl?: string; siteId?: string }) => {
  const { t } = useTranslation();
  const liveSiteUrl = siteUrl || '#';

  return (
    <Box direction="vertical" gap="SP3">
      <SectionHelper
        title={t("grav-how-title")}
        actionText=""
        onAction={() => {}}
        onClose={() => {}}
      >
        <Box direction="vertical" gap="SP2">
          <Text size="small">
            {t("grav-how-intro")}
          </Text>
          <Text size="small" secondary>
            To test it out: set your profile image at{' '}
            <a href="https://gravatar.com/profile" target="_blank" rel="noopener noreferrer">
              gravatar.com/profile
            </a>
            {' '}using the same email you'll use to sign up on this site, then sign up.
          </Text>
          <Box direction="vertical" gap="SP1">
            <Text size="small" weight="bold">
              {t("grav-step1-title")}
            </Text>
            <Text size="small" secondary>
              {t("grav-step1-desc")}
            </Text>
          </Box>
          <Box direction="vertical" gap="SP1">
            <Text size="small" weight="bold">
              {t("grav-step2-title")}
            </Text>
            <Text size="small" secondary>
              {t("grav-step2-desc")}
            </Text>
          </Box>
          <Box direction="vertical" gap="SP1">
            <Text size="small" weight="bold">
              {t("grav-step3-title")}
            </Text>
            <Text size="small" secondary>
              {t("grav-step3-desc")}
            </Text>
          </Box>
          
          <Box gap="SP2" direction="horizontal" marginTop="SP2">
            <Button
              as="a"
              href={liveSiteUrl}
              target="_blank"
              disabled={!siteUrl}
              prefixIcon={<ExternalLink />}
            >
              {t("grav-open-live")}
            </Button>
          </Box>
        </Box>
      </SectionHelper>
    </Box>
  );
};

function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

function App() {
  const { t } = useTranslation();
  const appId = "655104d6-d14c-42d8-8197-38384e647359";
  const upgradeUrl = `https://memberships.certifiedcode.us/${window.location.search}`;
  const [client, setClient] = React.useState(null as any);
  const [isUpgraded, setIsUpgraded] = React.useState(false);
  const [showMembersList, setShowMembersList] = React.useState(false);
  // Hard code the message content for Gravatar app
  const [message] = React.useState({
    _id: "gravatar-auto-profile-images",
    title: "",
    description: "",
    image: "https://secure.gravatar.com/avatar/00000000000000000000000000000000?d=identicon&f=y",
    redirectUrl: "https://www.wix.com"
  } as any);
  const token = new URLSearchParams(window.location.search).get("token");
  const [instanceData, setInstanceData] = React.useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  } as any);

  useEffect(() => {
    ReactTagManager.init(tagManagerArgs);
    initAnalytics();
    try {
      if (inIframe()) {
        var c = createClient({
          host: dashboard.host(),
          auth: dashboard.auth(),
          modules: {
            dashboard,
            embeddedScripts,
            appInstances,
          },
        });
        setClient(c);
      }
    } catch (error) {
      throw error;
    }
  }, []);

  useEffect(() => {
    // Get app instance data using Wix API
    if (client) {
      client.appInstances.getAppInstance()
        .then((data: any) => {
          setInstanceData(data);
          setIsUpgraded(data?.instance?.isFree === false);
        })
        .catch((error: any) => {
          console.error("Error getting app instance:", error);
        });
    }
  }, [client]);

  useEffect(() => {
    if (token && message?.redirectUrl) {
      window.location.href = `https://www.wix.com/installer/install?token=${token}&appId=${appId}&redirectUrl=${message.redirectUrl}`;
    }
  }, [token, message]);

  if (token) {
    return (
      <>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text={t("loading")} />
            </Box>
          </Page.Content>
        </Page>
      </>
    );
  }

  // Show members list page
  if (showMembersList) {
    return (
      <>
        <Page height="100vh">
          <Page.Content>
            <Box padding="SP6">
              <MembersList onBack={() => setShowMembersList(false)} />
            </Box>
          </Page.Content>
        </Page>
      </>
    );
  }

  return (
    <>
      <Card>
        <MarketingPageLayout
          removeImageHorizontalPadding
          removeImageVerticalPadding
          content={
            <Box height="840px" verticalAlign="middle">
              {message._id ? (
                <MarketingPageLayoutContent
                  title={t("grav-title")}
                  content={t("grav-content")}
                  actions={
                    <Box gap="SP2" direction="vertical">
                      {/* <Box gap="SP2">
                        <Button
                          suffixIcon={<Icons.Edit />}
                          disabled={!instanceData?.site?.siteId}
                          as="a"
                          href={URLS.editorUrl}
                          target="_blank"
                        >
                          {"Edit Site"}
                        </Button>
                        <Button
                          suffixIcon={<PremiumFilled />}
                          disabled={!instanceData?.instanceId}
                          skin={isUpgraded ? "premium-light" : "premium"}
                          as="a"
                          href={URLS.upgradeUrl}
                          target="_blank"
                        >
                          {isUpgraded ? "Manage Plan" : "Upgrade to set live"}
                        </Button>
                      </Box> */}
                      <Button
                        suffixIcon={<PremiumFilled />}
                        skin={isUpgraded ? "premium-light" : "premium"}
                        as="a"
                        href={upgradeUrl}
                        target="_blank"
                        onClick={() => track("upgrade_clicked")}
                      >
                        {isUpgraded ? t("manage-plan") : t("upgrade")}
                      </Button>
                      <GravatarToggle />
                      <Box gap="SP2" direction="horizontal" marginTop="SP2">
                        <Button
                          prefixIcon={<Add />}
                          onClick={() => setShowMembersList(true)}
                        >
                          {t("grav-add-members")}
                        </Button>
                      </Box>
                      <InfoSection siteUrl={instanceData?.site?.url} siteId={instanceData?.site?.siteId} />
                    </Box>
                  }
                />
              ) : (
                <MarketingPageLayoutContent
                  title={
                    <SkeletonGroup skin="light">
                      <Box gap="SP2" direction="vertical">
                        <SkeletonRectangle height={"72px"} width={"396px"} />
                      </Box>
                    </SkeletonGroup>
                  }
                  content={
                    <SkeletonGroup skin="light">
                      <Box gap="SP2" direction="vertical">
                        <SkeletonRectangle height={"48px"} width={"396px"} />
                      </Box>
                    </SkeletonGroup>
                  }
                  actions={
                    <Box gap="SP2">
                      <SkeletonGroup skin="light">
                        <SkeletonRectangle height={"36px"} width={"129px"} />
                      </SkeletonGroup>
                      <SkeletonGroup skin="light">
                        <SkeletonRectangle height={"36px"} width={"207px"} />
                      </SkeletonGroup>
                    </Box>
                  }
                />
              )}
            </Box>
          }
          image={
            <Image src={message.image} />
          }
        />
      </Card>
    </>
  );
}

export default App;
